import { models } from 'powerbi-client';
import ResizeObserver from 'resize-observer-polyfill';
import { compose } from 'fp-ts/lib/function';
import { mapOption } from 'fp-ts/lib/Array';
import { fromArray } from 'fp-ts/lib/NonEmptyArray';
import { embed } from './embedder';
import { groupViews } from './view';
import { createResponsivePage, activate, isActive } from './page';
import { merge, extend, bind, find, mapL, mapP, bindP, getOrThrow, debounce } from './utils';
/**
 * Base embedder config.
 */
const baseConfig = {
    type: 'report',
    embedUrl: 'https://app.powerbi.com/reportEmbed',
    tokenType: models.TokenType.Embed,
    permissions: models.Permissions.Read,
    settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: false
    }
};
/**
 * Embed a view-only, reponsive report in the specified element.
 *
 * The report should be formed with mutliple pages, providing appropriate
 * layouts for different view sizes.
 */
export const embedReport = (id, accessToken, container, opts = {}) => embed(container, merge(baseConfig, opts, { id, accessToken }))
    .then(registerEvents)
    .then(bindActions);
/**
 * Get a list of all pages within the report.
 */
const getPages = (report) => bind(report, report.getPages)()
    .then(groupViews)
    .then(mapOption(fromArray))
    .then(mapL(createResponsivePage));
/**
 * Lookup a page within a report by name.
 */
const getPage = (report) => (name) => getPages(report)
    .then(find(p => p.name === name))
    .then(getOrThrow(`Could not find page titled ${name}`));
/**
 * Set the page within a responsive report.
 */
const setPage = (report) => compose(bindP(activate), getPage(report));
/**
 * Get the currently active page within a report.
 */
const getActivePage = compose(mapP(
// Should never happen, but just in case...
getOrThrow('Could not find active page')), mapP(find(isActive)), getPages);
/**
 * Reactivate the current page, selecting the most appopriate view for the
 * given state.
 */
const resetView = compose(bindP(activate), getActivePage);
/**
 * Expose a set of actions on a reponsive report that are safe to be passed to
 * the outside world.
 */
const bindActions = report => ({
    setPage: setPage(report),
    getPages: () => getPages(report),
    getPage: () => getActivePage(report),
    getFilters: bind(report, report.getFilters),
    setFilters: bind(report, report.setFilters),
    reload: bind(report, report.reload),
    setAccessToken: bind(report, report.setAccessToken),
    fullscreen: bind(report, report.fullscreen),
    exitFullscreen: bind(report, report.exitFullscreen)
});
/**
 * Bind to resize events on the element that's housing a report and switch in
 * alternative layouts as required.
 */
const registerEvents = (report) => {
    const ro = new ResizeObserver(debounce(() => resetView(report)));
    ro.observe(report.iframe);
    return extend(report, { __resizeObserver: ro });
};
